import { pathConstants } from "../routes/pathContants";
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useContext } from "react";
import { platformHandler } from "./context";
import { getPlatform } from "../helpers";
import {
  CustomerIcon,
  ExpenseIcon,
  HomeIcon,
  InventoryIcon,
  MoreIcon,
  PaymentIcon,
  ReportIcon,
  AccountingsIcon,
  // SupportIcon,
  MarketsIcon,
  TemiSparkIcon,
} from "../assets/svgIcons";
import { useSelector } from "react-redux";
import { PiSpeakerLowDuotone } from "react-icons/pi";

const useAppSidebarItems = ({ isCollapseSidebar, setActiveSubMenu }) => {
  const { pathname } = useLocation();
  const { platform } = useContext(platformHandler);
  const navigate = useNavigate();
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const [search] = useSearchParams();
  const defaultInventoryPath = () => {
    if (search.get("store_id"))
      return (
        pathConstants.INVENTORY_PRODUCTS + "?store_id=" + search.get("store_id")
      );
    else if (storeList?.length > 0)
      return (
        pathConstants.INVENTORY_PRODUCTS + "?store_id=" + storeList?.[0]?.id
      );
    else return pathConstants.INVENTORY_PRODUCTS;
  };

  return {
    appSidebarItems: [
      {
        name: "Dashboard",
        key: 0,
        active: matchPath(getPlatform(platform) + "/dashboard/*", pathname),
        showOnLocalSetup: true,
        icon: (iconProp = { active: false }) => {
          return (
            <HomeIcon strokeColor={iconProp?.active ? "#7647ee" : undefined} />
          );
        },
        onClick: () => {
          navigate(pathConstants.DASHBOARD_MAIN);
          if (isCollapseSidebar) setActiveSubMenu(11);
        },
      },
      {
        name: "Inventory",
        key: 1,
        active:
          matchPath(getPlatform(platform) + "/inventory/*", pathname) ||
          matchPath(getPlatform(platform) + "/stores/*", pathname) ||
          matchPath(getPlatform(platform) + "/sections/*", pathname) ||
          matchPath(getPlatform(platform) + "/manage-sections/*", pathname) ||
          matchPath(getPlatform(platform) + "/procurement/*", pathname),
        showOnLocalSetup: true,
        icon: (iconProp = { active: false }) => {
          return (
            <InventoryIcon
              strokeColor={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(defaultInventoryPath());
          // setIsCollapseSidebar(false);
          setActiveSubMenu(11);
        },
        permissionParentKey: "Inventory Management",
        children: [
          {
            name: "Sell Mode",
            permissionParentKey: "Inventory Management",
            permissionChildKey: "can_view_stores",
            showOnLocalSetup: true,
            routePath: pathConstants.STORES,
            key: 10,
            showBorder: true,
          },
          {
            name: "Manage",
            permissionParentKey: "Inventory Management",
            showOnLocalSetup: true,
            key: 11,
            active: matchPath(getPlatform(platform) + "/inventory/*", pathname),
            children: [
              {
                name: "Products",
                key: 110,
                routePath: defaultInventoryPath(),
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_products",
                showOnLocalSetup: true,
              },
              {
                name: "Categories",
                key: 111,
                routePath: pathConstants.INVENTORY_CATEGORIES,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_product_category",
                showOnLocalSetup: true,
              },
              {
                name: "Stores",
                key: 112,
                routePath: pathConstants.INVENTORY_STORES,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_stores",
                showOnLocalSetup: true,
              },

              {
                name: "Stock Transfer",
                key: 113,
                routePath:
                  pathConstants.INVENTORY_PRODUCT_BULK_TRANSFER_HISTORY,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_stock_history",
                showOnLocalSetup: true,
              },
              {
                name: "Stock History",
                key: 114,
                routePath: pathConstants.INVENTORY_STOCK_HISTORY,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_stock_history",
                showOnLocalSetup: true,
              },
              {
                name: "Reconciliations",
                key: 115,
                routePath: pathConstants.INVENTORY_RECONCILIATIONS({}),
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_create_and_submit_reconciliation",
                showOnLocalSetup: true,
              },
              {
                name: "Stock Removals",
                key: 118,
                routePath: pathConstants.INVENTORY_STOCK_REMOVALS,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_stock_removals",
                showOnLocalSetup: true,
              },
              {
                name: "Products Batch",
                key: 116,
                routePath: pathConstants.INVENTORY_PRODUCTS_BATCH,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_products",
              },
              {
                name: "Track Items",
                key: 116,
                routePath:
                  pathConstants.INVENTORY_PRODUCT_ITEM_TRACKING +
                  "?is_sold=false",
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_products",
              },
            ],
          },
          {
            name: "Sales",
            key: 12,
            showOnLocalSetup: true,
            // showBorder: true,
            active: matchPath(getPlatform(platform) + "/sales/*", pathname),
            children: [
              {
                name: "Sales Transactions",
                key: 121,
                routePath:
                  pathConstants.INVENTORY_SALES_TRANSACTIONS +
                  "?order_source=INTERNAL",
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_sales_transaction",
                showOnLocalSetup: true,
              },
              {
                name: "Returns",
                key: 122,
                routePath: pathConstants.INVENTORY_RETURNS,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_returns",
              },
            ],
          },
          {
            name: "Procurement",
            key: 13,
            // showBorder: true,
            active: matchPath(
              getPlatform(platform) + "/procurement/*",
              pathname,
            ),
            children: [
              {
                name: "Fast-Track",
                key: 131,
                routePath: pathConstants.INVENTORY_RESTOCK_LOGS,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_stock_history",
              },
              {
                name: "Purchase Orders",
                key: 132,
                routePath:
                  pathConstants.INVENTORY_PURCHASE_ORDER + "?status=ACCEPTED",
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_purchase_order",
              },
              {
                name: "Goods Received",
                key: 133,
                routePath:
                  pathConstants.INVENTORY_GOODS_RECEIVED + "?status=ACCEPTED",
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_goods_received",
              },
              {
                name: "Bills",
                key: 134,
                routePath: pathConstants.INVENTORY_BILLS + "?status=APPROVED",
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_bill",
              },
              {
                name: "Suppliers",
                key: 135,
                routePath: pathConstants.INVENTORY_SUPPLIERS,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_edit_and_create_suppliers",
              },
              {
                name: "Waybills",
                key: 136,
                routePath: pathConstants.PROCUREMENT_WAYBILLS,
              },
              {
                name: "Categories",
                key: 137,
                routePath: pathConstants.INVENTORY_PO_CATEGORIES,
                permissionParentKey: "Inventory Management",
              },
            ],
          },
          {
            name: "Finance",
            key: 14,
            showBorder: true,
            active: matchPath(
              getPlatform(platform) + "/procurement/*",
              pathname,
            ),
            children: [
              {
                name: "Analytics",
                key: 141,
                routePath: pathConstants.INVENTORY_ANALYTICS,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_analytics",
              },
              {
                name: "Store Wallets",
                key: 142,
                routePath: pathConstants.INVENTORY_STORE_WALLET_LIST,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_store_wallet",
                showOnLocalSetup: true,
              },
              {
                name: "Debtors",
                key: 143,
                routePath: pathConstants.INVENTORY_CREDITLOG,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_and_update_credits",
                showOnLocalSetup: true,
              },
            ],
          },
          {
            name: "Storefront",
            key: 15,
            routePath: pathConstants.INVENTORY_MENU,
            permissionParentKey: "Inventory Management",
            permissionChildKey: "can_view_edit_and_create_storefront",
            showBorder: true,
          },
          {
            name: "Restaurant",
            key: 16,
            routePath: pathConstants.INVENTORY_MENU,
            permissionParentKey: "Inventory Management",
            showBorder: true,
            children: [
              {
                name: "Section Display",
                routePath: pathConstants.SECTIONS,
                key: 161,
                showBorder: true,
              },
              {
                name: "Tables",
                key: 162,
                routePath: pathConstants.INVENTORY_TABLES,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_view_tables",
              },
              {
                name: "Manage Section",
                key: 163,
                routePath: pathConstants.MANAGE_SECTIONS,
                permissionParentKey: "Inventory Management",
                permissionChildKey: "can_create_kitchen_sections",
              },
            ],
          },
          {
            name: "Activity Logs",
            key: 17,
            routePath: pathConstants.SETTINGS_ACTIVITY_LOGS,
            permissionParentKey: "Settings",
            permissionChildKey: "can_view_activity_logs",
            // showBorder: true,
          },
        ],
      },
      {
        name: "Payments",
        key: 2,
        active: matchPath(getPlatform(platform) + "/payments/*", pathname),
        icon: (iconProp = { active: false, hover: false }) => {
          return (
            <PaymentIcon
              strokeColor={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(pathConstants.DASHBOARD_OVERVIEW + "?all_business=true");
          setActiveSubMenu(20);
          // setIsCollapseSidebar(false);
        },
        permissionParentKey: "Payments",
        dontCheckCountry: false,
        children: [
          {
            name: "Overview",
            key: 20,
            permissionParentKey: "Payments",
            permissionChildKey:
              "can_view_payment_dashboard_overview_cash_and_customer_analytics",
            active: matchPath(
              getPlatform(platform) + "/payments/overview/*",
              pathname,
            ),
            children: [
              {
                name: "Summary",
                key: 201,
                routePath:
                  pathConstants.DASHBOARD_OVERVIEW + "?all_business=true",
              },
              {
                name: "Cashflow Analytics",
                key: 202,
                routePath:
                  pathConstants.DASHBOARD_CASHFLOW_ANALYTICS +
                  "?all_business=true",
              },
              {
                name: "Customer Analytics",
                key: 203,
                routePath:
                  pathConstants.DASHBOARD_CUSTOMER_ANALYTICS +
                  "?all_business=true",
              },
            ],
          },
          {
            name: "Transactions",
            key: 21,
            permissionParentKey: "Payments",
            permissionChildKey: "can_view_transactions_history",
            onClick: () => {},
            routePath:
              pathConstants.PAYMENT_TRANSACTIONS_ALL + "?all_business=true",
          },
          {
            name: "Transfers",
            key: 22,
            permissionParentKey: "Payments",
            permissionChildKey: "can_view_transfer",
            active: matchPath(
              getPlatform(platform) + "/payments/transfers/*",
              pathname,
            ),
            children: [
              {
                name: "Approved Transfers",
                key: 221,
                routePath:
                  pathConstants.PAYMENT_TRANSFER_APPROVED +
                  "?status=accepted&all_business=true",
              },
              {
                name: "Pending Transfers",
                key: 222,
                routePath:
                  pathConstants.PAYMENT_TRANSFER_PENDING +
                  "?status=pending_approval&all_business=true",
              },
              {
                name: "Rejected Transfers",
                key: 223,
                routePath:
                  pathConstants.PAYMENT_TRANSFER_REJECTED +
                  "?status=rejected&all_business=true",
              },
              {
                name: "Saved Beneficiaries",
                key: 224,
                routePath:
                  pathConstants.PAYMENT_TRANSFER_SAVEDBENEFICIARY +
                  "?all_business=true",
              },
            ],
          },
          {
            name: "Sub Accounts",
            key: 23,
            permissionParentKey: "Payments",
            routePath: pathConstants.PAYMENT_SUBACCOUNTS,
          },
          {
            name: "POS",
            key: 24,
            permissionParentKey: "Payments",
            permissionChildKey: "can_view_and_request_pos",
            active: matchPath(
              getPlatform(platform) + "/payments/pos/*",
              pathname,
            ),
            children: [
              {
                name: "All Terminals",
                key: 241,
                routePath:
                  pathConstants.PAYMENT_POS_ALL_TERMINALS +
                  "?all_business=true",
              },
              {
                name: "Terminal Requests",
                key: 242,
                routePath:
                  pathConstants.PAYMENT_POS_REQUEST_TERMINAL +
                  "?all_business=true",
              },
            ],
          },
          {
            name: "Tags",
            key: 25,
            permissionParentKey: "Payments",
            permissionChildKey: "can_view_tags",
            routePath: pathConstants.PAYMENT_ALL_TAGS,
          },
        ],
      },
      {
        name: "Accounting",
        key: 3,
        active: matchPath(getPlatform(platform) + "/accountings/*", pathname),
        icon: (iconProp = { active: false, hover: false }) => {
          return (
            <AccountingsIcon
              strokeColor={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(pathConstants.CHART_OF_ACCOUNTS);
          // setIsCollapseSidebar(false);
        },
        permissionParentKey: "Accounting",
        dontCheckCountry: false,
        children: [
          {
            name: "Chart Of Accounts",
            key: 31,
            permissionParentKey: "Accounting",
            permissionChildKey: "can_allow_ledger_account",
            onClick: () => {},
            routePath: pathConstants.CHART_OF_ACCOUNTS,
          },
          {
            name: "Account Settings",
            key: 32,
            permissionParentKey: "Accounting",
            permissionChildKey: "can_view_account_mappings",
            routePath: pathConstants.ACCOUNT_SETTINGS,
          },
          {
            name: "Journal Entries",
            key: 33,
            permissionParentKey: "Accounting",
            permissionChildKey: "can_view_journal_entries",
            routePath: pathConstants.ACCOUNTING_JOURNAL_ENTRIES,
          },
          {
            name: "General Ledger",
            key: 34,
            permissionParentKey: "Accounting",
            permissionChildKey: "can_allow_ledger_account",
            routePath: pathConstants.ACCOUNTING_GENERAL_LEDGER,
          },
          // {
          //   name: "VAT Ledger",
          //   key: 35,
          //   routePath: pathConstants.ACCOUNTING_VAT_LEDGER,
          //   permissionParentKey: "Accounting",
          //   permissionChildKey: "can_view_vat_logs",
          //   showOnLocalSetup: false,
          // },
          {
            name: "Reports",
            key: 36,
            permissionParentKey: "Accounting",
            permissionChildKey: "can_view_accounting_reports",
            routePath: pathConstants.ACCOUNTING_REPORTS,
          },
        ],
      },
      {
        name: "Expenses",
        key: 4,
        active: matchPath(getPlatform(platform) + "/expenses/*", pathname),
        showOnLocalSetup: true,
        icon: (iconProp = { active: false }) => {
          return (
            <ExpenseIcon
              strokeColor={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(pathConstants.EXPENSES_LIST({}));
          // setIsCollapseSidebar(false);
        },
        permissionParentKey: "Expense",
        children: [
          {
            name: "Expenses",
            key: 40,
            permissionParentKey: "Expense",
            permissionChildKey: "can_create_expense",
            onClick: () => {},
            routePath: pathConstants.EXPENSES_LIST({}),
          },
          {
            name: "Analytics",
            key: 41,
            permissionParentKey: "Expense",
            permissionChildKey: "can_view_expense_analytics",
            onClick: () => {},
            routePath: pathConstants.EXPENSES_ANALYTICS,
          },
          {
            name: "Categories",
            key: 42,
            permissionParentKey: "Expense",
            permissionChildKey: "can_view_edit_and_create_expense_category",
            onClick: () => {},
            routePath: pathConstants.EXPENSES_CATEGORIES,
          },

          {
            name: "Departments",
            key: 43,
            permissionParentKey: "Expense",
            // permissionChildKey: "can_view_edit_and_create_expense_category",
            onClick: () => {},
            routePath: pathConstants.EXPENSES_DEPARTMENTS,
          },
        ],
      },
      {
        name: "Marketing",
        key: 6,
        active: matchPath(getPlatform(platform) + "/marketing/*", pathname),
        icon: (iconProp = { active: false }) => {
          return (
            <PiSpeakerLowDuotone
              fontSize={25}
              color={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(pathConstants.MARKETING_SMS_CAMPAIGNS_AUTOMATED);
          // setIsCollapseSidebar(false);
        },
        permissionParentKey: "Loyalty",
        children: [
          {
            name: "SMS Campaigns",
            key: 21,
            // permissionParentKey: "Payments",
            // permissionChildKey: "can_view_transactions_history",
            onClick: () => {},
            routePath: pathConstants.MARKETING_SMS_CAMPAIGNS_AUTOMATED,
          },
          {
            name: "Loyalty",
            key: 62,
            permissionParentKey: "Loyalty",
            active: matchPath(
              getPlatform(platform) + "/marketing/loyalty/*",
              pathname,
            ),
            children: [
              {
                name: "Loyalty Campaign",
                key: 61,
                onClick: () => {},
                routePath: pathConstants.LOYALTY_MANAGE_CAMPAIGN,
                permissionParentKey: "Loyalty",
                permissionChildKey: "can_view_and_create_loyalty_campaigns",
              },
              {
                name: "Giftcards",
                key: 62,
                onClick: () => {},
                routePath: pathConstants.LOYALTY_MANAGE_GIFTCARDS,
                permissionParentKey: "Loyalty",
                permissionChildKey: "can_view_and_create_gift_cards",
              },
            ],
          },
        ],
      },
      {
        name: "Performance",
        key: 5,
        active:
          matchPath(getPlatform(platform) + "/accounting/*", pathname) ||
          matchPath(getPlatform(platform) + "/performance/*", pathname),
        icon: (iconProp = { active: false }) => {
          return (
            <ReportIcon
              strokeColor={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(pathConstants.ACCOUNTING_OVERVIEW);
        },
        children: [
          {
            name: "Reports",
            permissionParentKey: "Inventory Management",
            routePath: pathConstants.ACCOUNTING_OVERVIEW,
            key: 51,
            showBorder: true,
          },
          {
            name: "Goals",
            routePath: pathConstants.PERFORMANCE_GOALS + "?status=active",
            key: 52,
            showBorder: true,
            permissionParentKey: "Inventory Management",
            permissionChildKey: "can_view_revenue_goal",
          },
        ],
      },

      {
        name: "Customers",
        key: 7,
        active: matchPath(getPlatform(platform) + "/customers/*", pathname),
        showOnLocalSetup: true,
        icon: (iconProp = { active: false }) => {
          return (
            <CustomerIcon
              strokeColor={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(pathConstants.CUSTOMERS);
        },
        permissionParentKey: "Customers",
        permissionChildKey: "can_view_customer",
      },
      {
        name: "Ask Temi",
        key: 7,
        active: matchPath(
          getPlatform(platform) + "/lumi-ai-assistant/*",
          pathname,
        ),
        showOnLocalSetup: true,
        // Temi is only ever the sparkle mark — never the Lumi wordmark, which
        // is reserved for the product logo at the top of the rail.
        icon: () => {
          return <TemiSparkIcon width="28" height="28" />;
        },
        onClick: () => {
          navigate(pathConstants.LUMI_AI_ASSISTANT);
        },
      },
      {
        name: "Apps",
        key: 48,
        active: matchPath(getPlatform(platform) + "/markets/*", pathname),
        icon: (iconProp = { active: false }) => {
          return (
            <MarketsIcon
              strokeColor={iconProp?.active ? "#7647ee" : undefined}
            />
          );
        },
        onClick: () => {
          navigate(pathConstants.APPS_MAIN);
        },
        permissionParentKey: "Settings",
      },
      // {
      //   name: "Support",
      //   active: matchPath(getPlatform(platform) + "/supports/*", pathname),
      //   key: 8,
      //   icon: (iconProp = { active: false }) => {
      //     return (
      //       <SupportIcon
      //         strokeColor={iconProp?.active ? "#7647ee" : undefined}
      //       />
      //     );
      //   },
      // },
      {
        name: "More",
        active: matchPath(getPlatform(platform) + "/settings/*", pathname),
        key: 9,
        icon: (iconProp = { active: false }) => {
          return (
            <MoreIcon strokeColor={iconProp?.active ? "#7647ee" : undefined} />
          );
        },
      },
    ].filter(Boolean),
  };
};
export default useAppSidebarItems;
